import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { money, formatDate } from "@/lib/format";
import { toast } from "sonner";

export default function OfferPage() {
  const { submissionId } = useParams();
  const [sub, setSub] = useState(null);
  const [acting, setActing] = useState(false);

  const load = () => api.get(`/seller-submissions/${submissionId}`).then((res) => setSub(res.data)).catch(() => setSub(false));

  useEffect(() => { load(); }, [submissionId]);

  if (sub === false) return <div className="container-lookers py-24 text-center text-zinc-900">Offer not found.</div>;
  if (!sub) return <div className="container-lookers py-24 text-center text-zinc-400">Loading…</div>;

  const expired = sub.offer_expiry && new Date(sub.offer_expiry) < new Date();
  const canAct = ["quote_sent", "revised_offer"].includes(sub.status) && !expired;

  const act = async (path) => {
    setActing(true);
    try {
      await api.post(`/seller-submissions/${submissionId}/${path}`);
      toast.success("Updated");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setActing(false);
    }
  };

  return (
    <div className="container-lookers py-16 max-w-xl mx-auto" data-testid="offer-page">
      <h1 className="font-serif text-4xl mb-2 text-zinc-900">
        We'll Buy Your {sub.brand}s
      </h1>
      <p className="text-sm text-zinc-500 mb-8">Submission {sub.submission_number} — {sub.status.replace(/_/g, " ")}</p>

      {sub.status === "declined" ? (
        <p className="text-sm text-zinc-800">You've declined this offer.</p>
      ) : sub.status === "closed" ? (
        <p className="text-sm text-zinc-800">This submission has been closed.</p>
      ) : sub.cash_offer == null ? (
        <p className="text-sm text-zinc-500">We're still preparing your offer — we'll email you as soon as it's ready.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="border border-zinc-200 rounded-sm bg-white p-6 text-center">
            <div className="text-xs font-heading uppercase text-zinc-400 mb-2">Cash Offer</div>
            <div className="font-serif text-4xl mb-4 text-zinc-900">{money(sub.cash_offer)}</div>
            <button disabled={!canAct || acting} onClick={() => act("accept-cash")} className="w-full bg-teal-600 hover:bg-teal-700 text-ink text-xs font-heading font-semibold uppercase py-3 rounded-full transition-colors disabled:opacity-40" data-testid="accept-cash-offer-button">
              Accept Cash Offer
            </button>
          </div>
          <div className="border border-emerald-200 rounded-sm p-6 text-center bg-emerald-50">
            <div className="text-xs font-heading uppercase text-zinc-400 mb-2">Trade-In Credit</div>
            <div className="font-serif text-4xl mb-4 text-zinc-900">{money(sub.trade_credit)}</div>
            <button disabled={!canAct || acting} onClick={() => act("accept-trade")} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-heading font-semibold uppercase py-3 rounded-full transition-colors disabled:opacity-40" data-testid="accept-trade-offer-button">
              Choose Trade-In Credit
            </button>
          </div>
        </div>
      )}

      {canAct && (
        <button disabled={acting} onClick={() => act("decline")} className="text-xs underline text-zinc-400" data-testid="decline-offer-button">Decline Offer</button>
      )}
      {sub.offer_expiry && <p className="text-xs text-zinc-400 mt-6">{expired ? "This offer has expired." : `Offer valid until ${formatDate(sub.offer_expiry)}.`}</p>}
      <p className="text-xs text-zinc-400 mt-4">This offer is based on the information and photographs you provided. When Lookers receives your glasses, our team performs a final physical inspection. If the item materially differs from your description, we may revise the offer and you'll be able to accept the revision or request a return.</p>

      {sub.tracking_number && (
        <div className="border border-zinc-200 rounded-sm bg-white p-5 mt-8">
          <div className="font-heading text-xs uppercase mb-2 text-teal-700">Your Postage Label Is Ready</div>
          <p className="text-sm text-zinc-500 mb-2">Tracking number: {sub.tracking_number} ({sub.carrier})</p>
          <p className="text-xs text-zinc-400">Please pack your frames securely, including any case if available, and attach the label to your parcel.</p>
        </div>
      )}
    </div>
  );
}
